import { useTranslation } from 'react-i18next';
import { Route, Routes, useLocation, useNavigate } from 'react-router-dom';
import PageWrapperMain from '../../components/page-wrappers/PageWrapperMain';
import SubPageContentHolder from '../../components/subPageContentHolder/SubPageContentHolder';
import SubPageInnerContentHolder from '../../components/subPageContentHolder/SubPageInnerContentHolder';
import { allRoutes } from '../../lib/constants/route';
import { useEffect, useState } from 'react';
import LoadingSpinner from '../../ui/loadingSpinner/LoadingSpinner';
import NoData from '../../ui/noData/NoData';
import { extensionSVG, pulseSVG } from '../../lib/allCustomSVGs';
import Popup from '../../ui/popup/Popup';
import AIOptimizationsAutomated from './AIOptimizationsAutomated/AIOptimizationsAutomated';
import AIOptimizationsManual from './AIOptimizationsManual/AIOptimizationsManual';
import '../../styles/pages/aiOptimizations/aiOptimizations.scss';
import AIOptimizationsGlobalSettingsCard from './AIOptimizationsGlobalSettingsCard';
import AIOptimizationsStatusCard from './AIOptimizationsStatusCard';
import AIOptimizationsChromeExtensionCard from './AIOptimizationsChromeExtensionCard';
import get_automated_recommendations_api_call from '../../lib/api/ai-tracker/get_automated_recommendations_api_call';
import get_manual_recommendations_api_call from '../../lib/api/ai-tracker/get_manual_recommendations_api_call';
import toggle_automated_recommendation_api_call from '../../lib/api/ai-tracker/toggle_automated_recommendation_api_call';
import update_manual_recommendation_status_api_call from '../../lib/api/ai-tracker/update_manual_recommendation_status_api_call';
import get_available_tracking_data_api_call from '../../lib/api/stats/get_available_tracking_data_api_call';
import { useSelector } from 'react-redux';
import store, { RootState } from '../../store/store';
import {
  AutomatedRecommendationsData,
  ManualRecommendation,
} from '../../interfaces/AIOptimizations';
import { RequestInfo } from '../../interfaces/RequestInfo';
import { CHECK_CONNECTION_API } from '../../lib/constants/central-api';
import { applicationWWWXRLEncodedBodyBuilder } from '../../lib/utils/body-builder';
import { asyncHandler } from '../../lib/api/async-handler';
import { setMessage } from '../../store/reducers/appSlice';

const AIOptimizations = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const ai_tracker_api_key = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_api_key
  );
  const ai_tracker_access_token = useSelector(
    (state: RootState) => state.user?.stats?.ai_tracker_access_token
  );
  const shopURL = useSelector(
    (state: RootState) => state.user?.company?.webshop_url
  );

  const [loading, setLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('pending');
  const [isSettingsLoading, setIsSettingsLoading] = useState<
    'pending' | 'success' | 'error' | 'null'
  >('success');
  const [trackingDataLoading, setTrackingDataLoading] = useState(true);

  const [isSettingsPopupOpen, setIsSettingsPopupOpen] = useState(false);
  const [isStatusPopupOpen, setIsStatusPopupOpen] = useState(false);
  const [isChromeExtensionPopupOpen, setIsChromeExtensionPopupOpen] =
    useState(false);
  const [isLoadingConnection, setIsLoadingConnection] = useState(false);
  const [isTrackingScriptConnected, setIsTrackingScriptConnected] =
    useState(false);
  const [isGoogleAnalyticsConnected, setIsGoogleAnalyticsConnected] =
    useState(false);

  const [automatedRecommendations, setAutomatedRecommendations] =
    useState<AutomatedRecommendationsData | null>(null);
  const [manualRecommendations, setManualRecommendations] = useState<
    ManualRecommendation[]
  >([]);

  useEffect(() => {
    if (location.pathname === '/ai-optimizations') {
      navigate('/ai-optimizations/automated');
    }
    if (location.search.includes('integrationsRedirect')) {
      setIsStatusPopupOpen(true);
      store.dispatch(
        setMessage({
          message: t('ConnectionSuccessfulGA'),
          messageType: 'success',
        })
      );
    }
  }, [location.pathname, navigate]);

  useEffect(() => {
    const checkAndLoadTrackingData = async () => {
      setTrackingDataLoading(true);

      if (!ai_tracker_api_key) {
        try {
          await get_available_tracking_data_api_call();
        } catch (error) {
          console.error('Error loading tracking data:', error);
          setLoading('error');
          setTrackingDataLoading(false);
          return;
        }
      }

      setTrackingDataLoading(false);
    };

    checkAndLoadTrackingData();
  }, [ai_tracker_api_key]);

  useEffect(() => {
    const loadAIOptimizationData = async () => {
      if (
        trackingDataLoading ||
        !ai_tracker_api_key ||
        !ai_tracker_access_token
      ) {
        return;
      }

      setLoading('pending');
      try {
        // Check if the tracking script is connected
        checkConnection();
        // Load automated recommendations
        const automatedData = await get_automated_recommendations_api_call();
        if (automatedData) {
          setAutomatedRecommendations(automatedData);
        }

        // Load manual recommendations
        const manualData = await get_manual_recommendations_api_call({
          per_page: 200, //! Pagination hardcoded for now, since the API is not working with pagination
          page: 1,
        });
        if (manualData) {
          setManualRecommendations(manualData);
        }

        const hasAutomatedData =
          automatedData &&
          (automatedData.pending.length > 0 ||
            Object.keys(automatedData.pending).length > 0 ||
            automatedData.active > 0 ||
            Object.keys(automatedData.active).length > 0 ||
            automatedData.paused.length > 0 ||
            Object.keys(automatedData.paused).length > 0 ||
            automatedData.rejected.length > 0 ||
            Object.keys(automatedData.rejected).length > 0);

        const hasManualData = manualData && manualData.length > 0;

        if (!hasAutomatedData && !hasManualData) {
          setLoading('null');
        } else {
          setLoading('success');
        }
      } catch (error) {
        console.error('Error loading AI optimization data:', error);
        setLoading('error');
      }
    };

    loadAIOptimizationData();
  }, [trackingDataLoading, ai_tracker_api_key, ai_tracker_access_token]);

  const toggleSettingsPopupModal = () => {
    setIsSettingsPopupOpen(!isSettingsPopupOpen);
  };

  const toggleStatusPopupModal = () => {
    setIsStatusPopupOpen(!isStatusPopupOpen);
  };

  const toggleChromeExtensionPopupModal = () => {
    setIsChromeExtensionPopupOpen(!isChromeExtensionPopupOpen);
  };

  const handleAutomatedRecommendationToggle = async (
    recommendationId: string,
    enabled: boolean
  ) => {
    try {
      const success = await toggle_automated_recommendation_api_call(
        recommendationId,
        enabled
      );
      if (success) {
        const automatedData = await get_automated_recommendations_api_call();
        if (automatedData) {
          setAutomatedRecommendations(automatedData);
        }
      }
    } catch (error) {
      console.error('Error toggling automated recommendation:', error);
    }
  };

  const handleManualRecommendationStatusUpdate = async (
    recommendationId: string,
    status: string
  ) => {
    try {
      const success = await update_manual_recommendation_status_api_call(
        recommendationId,
        status as any
      );
      if (success) {
        const manualData = await get_manual_recommendations_api_call({
          per_page: 200, //! Pagination hardcoded for now, since the API is not working with pagination
          page: 1,
        });
        if (manualData) {
          setManualRecommendations(manualData);
        }
      }
    } catch (error) {
      console.error('Error updating manual recommendation status:', error);
    }
  };

  const checkConnection = async (manualCheck?: boolean) => {
    setIsLoadingConnection(true);
    if (!ai_tracker_api_key || !shopURL || shopURL === '') {
      setIsTrackingScriptConnected(false);
      setIsLoadingConnection(false);
      return;
    }

    try {
      const sendingData = {
        tracking_id: ai_tracker_api_key,
        site_url: shopURL,
      };

      const requestInfo: RequestInfo = {
        url: CHECK_CONNECTION_API,
        method: 'POST',
        body: applicationWWWXRLEncodedBodyBuilder(sendingData),
        headers: {
          Accept: 'application/json',
          'Content-type': 'application/x-www-form-urlencoded',
        },
      };

      const actionType = 'CHECK_CONNECTION_API';
      const customMsg = '';

      const res: any = await asyncHandler(
        requestInfo,
        actionType,
        customMsg,
        false,
        false,
        false,
        false,
        [],
        true
      );

      const response = res.data;

      if (
        res.actionType === actionType &&
        response?.success === true &&
        response?.data?.script_detected
      ) {
        setIsTrackingScriptConnected(true);
        if (manualCheck) {
          store.dispatch(
            setMessage({
              message: t('Tracking-Script-Connected-msg'),
              messageType: 'success',
            })
          );
        }
      } else {
        setIsTrackingScriptConnected(false);
        if (manualCheck) {
          store.dispatch(
            setMessage({
              message: t('Tracking-Script-Not-Connected-msg'),
              messageType: 'warning',
            })
          );
        } else {
          setIsStatusPopupOpen(true);
        }
      }
      setIsLoadingConnection(false);
    } catch (error) {
      console.error('Error fetching tracking data:', error);
      setIsTrackingScriptConnected(false);
      setIsLoadingConnection(false);
    }
    return;
  };

  const isAutomated = location.pathname.includes('/ai-optimizations/automated');
  const isManual = location.pathname.includes('/ai-optimizations/manual');

  const pageTitle = isAutomated
    ? t('AIOptimizeTitleAutomated')
    : isManual
    ? t('AIOptimizeTitleManual')
    : t('AIOptimizeTitle');

  const pageSubTitle = isAutomated
    ? t('AIOptimizeSubTitleAutomated')
    : isManual
    ? t('AIOptimizeSubTitleManual')
    : '';

  if (trackingDataLoading || !ai_tracker_api_key || !ai_tracker_access_token) {
    return (
      <PageWrapperMain>
        <SubPageContentHolder>
          <SubPageInnerContentHolder title={pageTitle} subTitle={pageSubTitle}>
            <LoadingSpinner />
          </SubPageInnerContentHolder>
        </SubPageContentHolder>
      </PageWrapperMain>
    );
  }

  return (
    <PageWrapperMain>
      <SubPageContentHolder>
        <SubPageInnerContentHolder
          title={pageTitle}
          subTitle={pageSubTitle}
          mainButtonType='white'
          mainBtnTitle={t('IntegrationStatus')}
          mainBtnIcon={pulseSVG}
          mainBtnLoading={false}
          mainBtnAction={() => {
            if (isSettingsLoading === 'success') {
              setIsSettingsPopupOpen(false);
              setIsChromeExtensionPopupOpen(false);
              setIsStatusPopupOpen(true);
            }
          }}
          secondaryButtonType='white'
          secondaryBtnTitle={t('ChromeExtensionTitle')}
          secondaryBtnIcon={extensionSVG}
          secondaryBtnLoading={false}
          secondaryBtnAction={() => {
            if (isSettingsLoading === 'success') {
              setIsSettingsPopupOpen(false);
              setIsStatusPopupOpen(false);
              setIsChromeExtensionPopupOpen(true);
            }
          }}
        >
          {loading === 'pending' && <LoadingSpinner />}
          {loading === 'null' && <NoData title={t('NoAIOptimizations')} />}
          {loading === 'error' && (
            <NoData
              title={t('ErrorFetchingData')}
              subTitle={t('SomethingWentWrong-msg')}
            />
          )}

          <Popup
            styles={{ backgroundColor: '#fff' }}
            setTrigger={toggleSettingsPopupModal}
            trigger={isSettingsPopupOpen}
          >
            <AIOptimizationsGlobalSettingsCard
              toggleSettingsPopupModal={toggleSettingsPopupModal}
            />
          </Popup>
          <Popup
            styles={{ backgroundColor: '#fff', maxHeight: '90vh' }}
            setTrigger={toggleStatusPopupModal}
            trigger={isStatusPopupOpen}
          >
            <AIOptimizationsStatusCard
              isTrackingScriptConnected={isTrackingScriptConnected}
              isLoadingConnection={isLoadingConnection}
              checkConnection={() => {
                checkConnection(true);
              }}
              apiKey={ai_tracker_api_key}
              isGoogleAnalyticsConnected={isGoogleAnalyticsConnected}
              setIsGoogleAnalyticsConnected={setIsGoogleAnalyticsConnected}
            />
          </Popup>

          <Popup
            styles={{ backgroundColor: '#fff', maxHeight: '90vh' }}
            setTrigger={toggleChromeExtensionPopupModal}
            trigger={isChromeExtensionPopupOpen}
          >
            <AIOptimizationsChromeExtensionCard
              toggleChromeExtensionPopupModal={toggleChromeExtensionPopupModal}
            />
          </Popup>

          <Routes>
            {allRoutes['/ai-optimizations/automated'] && (
              <Route
                path='automated/*'
                element={
                  loading === 'success' ? (
                    <AIOptimizationsAutomated
                      automatedRecommendations={automatedRecommendations}
                      onRecommendationToggle={
                        handleAutomatedRecommendationToggle
                      }
                    />
                  ) : null
                }
              />
            )}
            {allRoutes['/ai-optimizations/manual'] && (
              <Route
                path='manual/*'
                element={
                  loading === 'success' ? (
                    <AIOptimizationsManual
                      manualRecommendations={manualRecommendations}
                      onStatusUpdate={handleManualRecommendationStatusUpdate}
                    />
                  ) : null
                }
              />
            )}
          </Routes>
        </SubPageInnerContentHolder>
      </SubPageContentHolder>
    </PageWrapperMain>
  );
};

export default AIOptimizations;
